import React from 'react'
import {
  Grid,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Grow,
  Alert,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { Trans, useTranslation } from 'react-i18next'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { EMAIL_REGEX } from 'helpers/constants'
import UserService from 'api/services/User'
interface FormFields {
  email: string
}

const DefaultValues = {
  email: '',
}

function ResetPassword(props) {
  const { visibility } = props

  const { executeRecaptcha } = useGoogleReCaptcha()

  const createGoogleToken = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    return await executeRecaptcha()
  }, [executeRecaptcha])

  const { t } = useTranslation()

  const { enqueueSnackbar } = useSnackbar()

  const {
    control,
    reset,
    handleSubmit: handleSubmitResetPassword,
  } = useForm<FormFields>({
    defaultValues: DefaultValues,
  })

  React.useEffect(() => {
    requestResetPasswordMutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!visibility) {
      requestResetPasswordMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility])

  const requestResetPasswordMutation =
    UserService.hooks.useRequestResetPassword({
      onSuccess: () => {
        reset()
      },
      onError: (err) => {
        enqueueSnackbar(t('tables.responses.error') as unknown as string, {
          variant: 'error',
        })
      },
    })

  const resetPasswordSubmit = async (data) => {
    requestResetPasswordMutation.reset()
    const token = (await createGoogleToken()) ?? ''
    requestResetPasswordMutation.mutate({ email: data.email, token })
  }

  return (
    <Grow in={visibility} unmountOnExit>
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Controller
            rules={{
              required: t('validation.fieldRequired') as unknown as string,
              pattern: {
                value: EMAIL_REGEX,
                message: t('validation.emailPatternError') as unknown as string,
              },
            }}
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                label={t('signInPage.form.email')}
                variant="filled"
                error={!!error}
                fullWidth
                helperText={error ? error?.message : ' '}
                sx={{
                  my: 2,
                  '& .MuiFilledInput-input': {
                    fontSize: 20,
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: 20,
                  },
                }}
                {...field}
              ></TextField>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              textTransform: 'none',
              py: 2,
              minHeight: 70,
            }}
            onClick={handleSubmitResetPassword(resetPasswordSubmit)}
          >
            {requestResetPasswordMutation.isLoading ? (
              <CircularProgress sx={{ color: 'background.paper' }} size={25} />
            ) : (
              <Typography fontSize={25}>
                {' '}
                {t('signInPage.forgotPassword.resetButtonText')}
              </Typography>
            )}
          </Button>
        </Grid>

        {requestResetPasswordMutation.isSuccess ? (
          <Grid item xs={12} sx={{ my: 4 }}>
            <Alert severity="success">
              <Typography
                display="block"
                component="span"
                color="text.primary"
                fontSize={20}
              >
                {t(`signInPage.forgotPassword.messages.success`)}
              </Typography>
              <Typography
                display="block"
                component="span"
                variant="caption"
                color="secondary"
                fontSize={16}
              >
                <Trans i18nKey={`signInPage.forgotPassword.messages.spam`} />
              </Typography>
            </Alert>
          </Grid>
        ) : null}
      </Grid>
    </Grow>
  )
}

export default ResetPassword
